import { api } from '@/shared/api/client'
import { qs } from '@/shared/lib/query'

/**
 * The platform calendar — what happened, everywhere, on a day.
 *
 * Each source is gated by its own feature's permission on the server, so `sources` is what
 * THIS caller may see rather than a fixed list. The filter chips are built from it.
 */

export interface CalendarSource {
  code: string
  label: string
}

export interface CalendarItem {
  sourceCode: string
  /** Null for aggregated rows — "47 interviews captured" is not one row anywhere. */
  id: string | null
  day: string
  occurredAt: string | null
  title: string
  detail: string | null
  /** 1 for a rally; 47 for "47 interviews". Drives the busy-ness of the day. */
  count: number
  statusCode: string | null
  unitId: string | null
  unitName: string | null
  route: string | null
}

/** A day in the MONTH grid — counts only. The grid never needed the rows. */
export interface CalendarDay {
  date: string
  total: number
  bySource: Record<string, number>
}

/** One day, in full. Fetched only when a day is opened. */
export interface CalendarDayDetail {
  date: string
  total: number
  bySource: Record<string, number>
  items: CalendarItem[]
  /**
   * Rows dropped past the per-source cap. A day that hits it says so — the counts above
   * describe the whole day, so rows that silently stopped would disagree with them.
   */
  truncated: Record<string, number>
  sources: CalendarSource[]
  unavailable: string[]
}

export interface CalendarRange {
  from: string
  to: string
  days: CalendarDay[]
  sources: CalendarSource[]
  /** Sources that threw. Named, so a short day is never silently short. */
  unavailable: string[]
}

export const calendarBetween = (p: { from: string, to: string, source?: string[] }) =>
  api.get<CalendarRange>(`/calendar${qs(p)}`)

export const calendarDay = (p: { date: string, source?: string[] }) =>
  api.get<CalendarDayDetail>(`/calendar/day${qs(p)}`)

export const calendarSources = () => api.get<CalendarSource[]>('/calendar/sources')
